import React, { useEffect, useState } from "react";

import { OAUTH_CHANNEL } from "./api/mcp";

import type { McpOAuthReturn } from "@/shared/types";

/* `/oauth/mcp/callback` — where an MCP server's sign-in sends the popup back.

   It hands `code` and `state` to the dialog that opened it, over a
   BroadcastChannel (a sign-in page with Cross-Origin-Opener-Policy cuts
   `window.opener`, a channel survives it), and closes. It calls no API and
   stores nothing: the dialog completes the sign-in, as the person who started
   it. Outside the auth guard, because a popup has no reason to boot the app. */
function readReturn(): McpOAuthReturn {
  const query = new URLSearchParams(window.location.search);
  return {
    code: query.get("code"),
    state: query.get("state"),
    error: query.get("error_description") ?? query.get("error"),
  };
}

export default function McpOAuthCallback() {
  // read once: the effect wipes the query, and may run twice in development
  const [back] = useState(readReturn);

  useEffect(() => {
    const channel = new BroadcastChannel(OAUTH_CHANNEL);
    channel.postMessage(back);
    channel.close();
    // the code must not linger in the address bar or the history
    window.history.replaceState(null, "", window.location.pathname);
    const timer = window.setTimeout(() => window.close(), 600);
    return () => window.clearTimeout(timer);
  }, [back]);

  return (
    <div className="h-screen w-screen flex items-center justify-center bg-[var(--surface-sunk)] px-6">
      <div className="max-w-[340px] text-center">
        <div className="text-[15px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">
          {back.error ? "The sign-in did not complete" : "Signed in"}
        </div>
        <div className="text-[13px] text-[var(--text-tertiary)] mt-1">
          {back.error ?? "You can close this window — the console is finishing the connection."}
        </div>
      </div>
    </div>
  );
}
